// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, Spinner, Table, Text, TextInput, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BasePagination, Loading } from '~/components/common';
import ModalDeleteExam from './component/ModalDeleteExam';
import BaseDynamicSelect, {
  IBaseSelectPayload,
  IBaseSelectResult,
} from '~/components/common/specific/baseDynamicSelect/BaseDynamicSelect';
// Others
import { IDiscussionForm } from '~/utils/mockData/common';
import { defaultValuesForm, schema } from './helper';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getStudents } from '~/thunks/user/userThunk';
import { DEFAULT_LIMIT_PAGE, DEFAULT_PAGE, TIMEOUT_ALERT_MILLISECOND } from '~/utils/constants/common';
import { getCourses, getDiscussionsOfCourse } from '~/thunks/course/courseThunk';
import { IBaseSelectOption } from '~/utils/interface/common';
import { generateGuid } from '~/utils/helper/common';
import { IToast } from '~/utils/interface/toast';
import { toastActions } from '~/thunks/toast/toastSlice';
import {
  createDiscussionForumGrading,
  getListDiscussionForumGrading,
  updateDiscussionForumGrading,
} from '~/thunks/discussionForumGrading/discussionForumGradingThunk';
import { studentRoute } from '~/utils/routes/student';
import {
  IBodyCreateDiscussionForumGrade,
  IBodyUpdateDiscussionForumGrade,
  IQueryGetDiscussionForumGrading,
} from '~/utils/interface/discussion';
// Styles, images, icons
import styles from './Discussion.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const Discussion = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/administrativeArea', 'teacher/discussion']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<IDiscussionForm>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { discussionForumGrades, isLoadingCreateDiscussionForumGrading, isRefreshListDiscussionForumGrading, hasMore } =
    useAppSelector((state) => state.discussionForumGradingState);
  //#endregion Selector

  //#region Declare State
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [editingDiscussionId, setEditingDiscussionId] = useState<string | null>(null);
  const [isLoadingUpdateDiscussion, setIsLoadingUpdateDiscussion] = useState(false);
  const [isLoadingEditDiscussion, setIsLoadingEditDiscussion] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoadingOverlay, setIsLoadingOverlay] = useState(false);
  const [discussionIdToDelete, setDiscussionIdToDelete] = useState<string | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const courseIdForFetch = watch('courseId');
  const courseNameForLabel = watch('courseName');
  const discussionNameForLabel = watch('discussionName');
  const studentNameForLabel = watch('studentName');

  const fetchCourseOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      try {
        const resp = await dispatch(getCourses({ page: payload.page, limit: DEFAULT_LIMIT_PAGE })).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((item) => ({
          value: String(item.id),
          label: item.name,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch]
  );

  const fetchStudentOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      if (!courseIdForFetch) return { optionsResp: [], hasMoreResp: false };

      try {
        const resp = await dispatch(
          getStudents({ courseId: courseIdForFetch, page: payload.page, limit: DEFAULT_LIMIT_PAGE })
        ).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((item) => ({
          value: String(item.id),
          label: item.fullName,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch, courseIdForFetch]
  );

  const fetchDiscussionOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      if (!courseIdForFetch) return { optionsResp: [], hasMoreResp: false };

      try {
        const resp = await dispatch(
          getDiscussionsOfCourse({ courseId: courseIdForFetch, page: payload.page, limit: DEFAULT_LIMIT_PAGE })
        ).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((item) => ({
          value: String(item.id),
          label: item.name,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch, courseIdForFetch]
  );

  //#region Implement Hook
  useEffect(() => {
    handleGetDiscussionForumGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListDiscussionForumGrading]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleEditDiscussion = async (discussionId: string) => {
    setEditingDiscussionId(discussionId);
    setIsLoadingEditDiscussion(true);

    try {
      const discussionForumGrade = discussionForumGrades.find((item) => String(item.id) === String(discussionId));

      if (!discussionForumGrade) {
        setIsLoadingEditDiscussion(false);
        return;
      }

      setValue('studentId', String(discussionForumGrade.studentId));
      setValue('studentName', discussionForumGrade.studentName);
      setValue('courseId', String(discussionForumGrade.courseId));
      setValue('courseName', discussionForumGrade.courseName);
      setValue('discussionId', String(discussionForumGrade.discussionId));
      setValue('discussionName', discussionForumGrade.discussionName);
      setValue('gradingPoint', discussionForumGrade.gradingPoint.toString());
    } catch (error) {
      // Handle error - maybe show toast or reset editing state
      setEditingDiscussionId(null);
    } finally {
      setIsLoadingEditDiscussion(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDiscussionId(null);
    setIsLoadingEditDiscussion(false);
    handleResetForm();
  };

  const handleResetForm = () => {
    reset();
  };

  const handleUpdate = async (discussionId: string, payload: IBodyUpdateDiscussionForumGrade) => {
    setIsLoadingUpdateDiscussion(true);
    await dispatch(updateDiscussionForumGrading({ gradingId: discussionId, body: payload }))
      .unwrap()
      .then((res) => {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_update_success_message', { ns: 'common/base' }),
          type: 'success',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);

        setEditingDiscussionId(null);
        handleResetForm();
        handleGetDiscussionForumGrades();
      })
      .catch((error) => {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_update_error_message', { ns: 'common/base' }),
          type: 'error',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);
      })
      .finally(() => {
        setIsLoadingUpdateDiscussion(false);
      });
  };

  const handleDeleteDiscussion = (discussionId: string) => {
    setDiscussionIdToDelete(discussionId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDiscussionIdToDelete(null);
  };

  const handleDeleteSuccess = () => {
    handleGetDiscussionForumGrades();
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setIsLoadingOverlay(true);
    handleGetDiscussionForumGrades({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const handleGetDiscussionForumGrades = async (query?: IQueryGetDiscussionForumGrading) => {
    await dispatch(
      getListDiscussionForumGrading(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    )
      .unwrap()
      .finally(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleSelectCourse = async (option: IBaseSelectOption) => {
    setValue('courseId', option.value);
    setValue('courseName', option.label);
    setValue('studentId', '');
    setValue('studentName', '');
    setValue('discussionId', '');
    setValue('discussionName', '');
  };

  const handleBlurCreditInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    if (!value) {
      setValue('gradingPoint', '');
      return;
    }

    const numberValue = parseFloat(value);
    const formatGradingPoint = (value: number) => {
      if (!isFinite(value)) return '';
      if (value < 1 || value > 5) return '';
      return value.toString();
    };
    setValue('gradingPoint', formatGradingPoint(numberValue));
  };

  const handleNavigateToDiscussion = (courseId: number | string, discussionId: number | string) => {
    const url = `${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.discussion}/${discussionId}`;
    window.open(url, '_blank');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCreateDiscussionForumGrading = async (payload: IBodyCreateDiscussionForumGrade) => {
    await dispatch(createDiscussionForumGrading(payload))
      .unwrap()
      .then((res) => {
        handleResetForm();
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleSubmitForm = (data: IDiscussionForm) => {
    if (!data) return;

    if (editingDiscussionId) {
      // Update mode
      const payload: IBodyUpdateDiscussionForumGrade = {
        studentId: +data.studentId,
        studentName: data.studentName,
        courseId: +data.courseId,
        courseName: data.courseName,
        discussionId: +data.discussionId,
        discussionName: data.discussionName,
        gradingPoint: +data.gradingPoint,
      };
      handleUpdate(editingDiscussionId, payload);
    } else {
      // Create mode
      const payload: IBodyCreateDiscussionForumGrade = {
        studentId: +data.studentId,
        studentName: data.studentName,
        courseId: +data.courseId,
        courseName: data.courseName,
        discussionId: +data.discussionId,
        discussionName: data.discussionName,
        gradingPoint: +data.gradingPoint,
      };
      handleCreateDiscussionForumGrading(payload);
    }
  };

  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('header')} width={'100%'}>
          <View as='div' className={cx('title')}>
            <Heading level='h3'>{t('discussion_title', { ns: 'teacher/discussion' })}</Heading>
            <img
              className={cx('iconArea')}
              src={icons.commonIconExamArea}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </View>
        </View>

        <View as='div' className={cx('administrativeForm')} margin='medium 0' width={'100%'}>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <Flex direction={'column'} gap='medium' width={'100%'}>
              <View as='div' className={cx('administrativeFirstContainer')}>
                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='courseId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('discussion_select_course_label', { ns: 'teacher/discussion' })}
                        </label>
                        {isLoadingEditDiscussion ? (
                          <Flex alignItems='center' gap='small'>
                            <Spinner size='x-small' />
                          </Flex>
                        ) : (
                          <BaseDynamicSelect
                            value={value}
                            callApiFn={fetchCourseOptions}
                            selectedLabel={courseNameForLabel}
                            onChange={(option) => {
                              if (!option) return;
                              onChange(option.value);
                              handleSelectCourse(option);
                            }}
                          />
                        )}
                        {errors?.courseId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.courseId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='studentId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('discussion_select_student_label', { ns: 'teacher/discussion' })}
                        </label>
                        {isLoadingEditDiscussion ? (
                          <Flex alignItems='center' gap='small'>
                            <Spinner size='x-small' />
                          </Flex>
                        ) : (
                          <BaseDynamicSelect
                            key={courseIdForFetch}
                            value={value}
                            callApiFn={fetchStudentOptions}
                            selectedLabel={studentNameForLabel}
                            onChange={(option) => {
                              if (!option) return;
                              onChange(option.value);
                              setValue('studentName', option.label);
                            }}
                          />
                        )}
                        {errors?.studentId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.studentId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>
              </View>

              <View as='div' className={cx('administrativeSecondContainer')}>
                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='discussionId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('discussion_select_discussion_label', { ns: 'teacher/discussion' })}
                        </label>
                        {isLoadingEditDiscussion ? (
                          <Flex alignItems='center' gap='small'>
                            <Spinner size='x-small' />
                          </Flex>
                        ) : (
                          <BaseDynamicSelect
                            key={courseIdForFetch}
                            value={value}
                            callApiFn={fetchDiscussionOptions}
                            selectedLabel={discussionNameForLabel}
                            onChange={(option) => {
                              if (!option) return;
                              onChange(option.value);
                              setValue('discussionName', option.label);
                            }}
                            renderOptionSuffix={(option, isHighlighted) => (
                              <button
                                type='button'
                                onClick={() => handleNavigateToDiscussion(courseIdForFetch, option.value)}
                                className={cx('goToButton', { highlighted: isHighlighted })}
                              >
                                {t('discussion_go_to_button', { ns: 'teacher/discussion' })}
                              </button>
                            )}
                          />
                        )}
                        {errors?.discussionId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.discussionId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='gradingPoint'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('discussion_insert_point_label', { ns: 'teacher/discussion' })}
                        </label>
                        <TextInput
                          value={value}
                          onBlur={handleBlurCreditInput}
                          onChange={(e) => onChange(e.target.value)}
                        />
                        {errors?.gradingPoint?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.gradingPoint?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>
              </View>
            </Flex>

            <Flex justifyItems='center' margin='medium 0 0 0'>
              <View as='div' className={cx('administrativeButton')}>
                {editingDiscussionId ? (
                  <Flex gap='small'>
                    <Button
                      color='secondary'
                      type='button'
                      onClick={handleCancelEdit}
                      disabled={
                        isLoadingUpdateDiscussion || isLoadingCreateDiscussionForumGrading || isLoadingEditDiscussion
                      }
                    >
                      {t('cancel_button_label', { ns: 'common/base' })}
                    </Button>
                    <Button
                      color='primary'
                      type='submit'
                      disabled={
                        isLoadingUpdateDiscussion || isLoadingCreateDiscussionForumGrading || isLoadingEditDiscussion
                      }
                      renderIcon={
                        (isLoadingUpdateDiscussion ||
                          isLoadingCreateDiscussionForumGrading ||
                          isLoadingEditDiscussion) && <Spinner size='x-small' />
                      }
                    >
                      {t('save_button_label', { ns: 'common/base' })}
                    </Button>
                  </Flex>
                ) : (
                  <Button
                    color='primary'
                    type='submit'
                    disabled={isLoadingCreateDiscussionForumGrading}
                    renderIcon={isLoadingCreateDiscussionForumGrading && <Spinner size='x-small' />}
                  >
                    {t('discussion_add_button_label', { ns: 'teacher/discussion' })}
                  </Button>
                )}
              </View>
            </Flex>
          </form>
        </View>

        <View className={cx('examTableDesktop')}>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='studentId'>
                  {t('discussion_select_student_id_label_header', { ns: 'teacher/discussion' })}
                </Table.ColHeader>
                <Table.ColHeader id='studentName'>
                  {t('discussion_select_student_label_header', { ns: 'teacher/discussion' })}
                </Table.ColHeader>
                <Table.ColHeader id='courseName'>
                  {t('discussion_select_course_label_header', { ns: 'teacher/discussion' })}
                </Table.ColHeader>
                <Table.ColHeader id='moduleName'>
                  {t('discussion_select_module_label_header', { ns: 'teacher/discussion' })}
                </Table.ColHeader>
                <Table.ColHeader id='discussionName'>
                  {t('discussion_select_discussion_label_header', { ns: 'teacher/discussion' })}
                </Table.ColHeader>
                <Table.ColHeader id='gradingPoint'>
                  {t('discussion_point_label_header', { ns: 'teacher/discussion' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {discussionForumGrades.map((data, index) => (
                <Table.Row
                  key={index}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleNavigateToDiscussion(data.courseId, data.discussionId)}
                >
                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.studentId}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.studentName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.courseName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.modules?.map((module) => module.name).join(', ')}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.discussionName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.gradingPoint}</span>
                    </div>
                  </Table.Cell>

                  {/* <Table.Cell>
                    <Flex gap='small' alignItems='center'>
                      <IconButton
                        withBorder={false}
                        withBackground={false}
                        color='primary'
                        onClick={() => handleEditDiscussion(data.id)}
                        screenReaderLabel={t('common_edit_label', { ns: 'common/base' })}
                      >
                        <IconEditLine />
                      </IconButton>
                      <IconButton
                        withBorder={false}
                        withBackground={false}
                        color='danger'
                        onClick={() => handleDeleteDiscussion(data.id)}
                        screenReaderLabel={t('common_delete_label', { ns: 'common/base' })}
                      >
                        <IconTrashLine />
                      </IconButton>
                    </Flex>
                  </Table.Cell> */}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </View>

        <View className={cx('paginationDesktop')}>
          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>

        <View className={cx('examTableMobile')}>
          {discussionForumGrades.map((data, index) => (
            <View
              key={index}
              as='div'
              className={cx('card')}
              style={{ cursor: 'pointer' }}
              onClick={() => handleNavigateToDiscussion(data.courseId, data.discussionId)}
            >
              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('discussion_select_student_id_label', { ns: 'teacher/discussion' })}
                </span>
                <span>{data.studentId}</span>
              </View>
              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('discussion_select_student_label', { ns: 'teacher/discussion' })}
                </span>
                <span>{data.studentName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('discussion_select_course_label', { ns: 'teacher/discussion' })}
                </span>
                <span>{data.courseName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('discussion_select_module_label', { ns: 'teacher/discussion' })}
                </span>
                <span>{data.modules?.map((module) => module.name).join(', ')}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('discussion_select_discussion_label', { ns: 'teacher/discussion' })}
                </span>
                <span>{data.discussionName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>{t('discussion_point_label', { ns: 'teacher/discussion' })}</span>
                <span>{data.gradingPoint}</span>
              </View>

              {/* <Flex gap='small' alignItems='center' margin='small 0'>
                <IconButton
                  withBorder={false}
                  withBackground={false}
                  color='primary'
                  onClick={() => handleEditDiscussion(data.id)}
                  screenReaderLabel={t('common_edit_label', { ns: 'common/base' })}
                >
                  <IconEditLine />
                </IconButton>
                <IconButton
                  withBorder={false}
                  withBackground={false}
                  color='danger'
                  onClick={() => handleDeleteDiscussion(data.id)}
                  screenReaderLabel={t('common_delete_label', { ns: 'common/base' })}
                >
                  <IconTrashLine />
                </IconButton>
              </Flex> */}
            </View>
          ))}

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>
      </div>

      <ModalDeleteExam
        isModalOpen={isDeleteModalOpen}
        examId={discussionIdToDelete}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={handleDeleteSuccess}
      />

      <Loading loading={isLoadingOverlay} overlay={true} />
    </div>
  );
};

export default Discussion;
